import { useCallback, useRef } from 'react';
import { useApi } from '@paradigm/api';

import { useDrfqService } from '#/unified-rfqs/contexts/drfqs';

import {
  DrfqVenueCode,
  isFutureInstrument,
  isOptionInstrument,
} from '#/unified-rfqs/entities/drfq/domain';
import { Instrument } from '#/unified-rfqs/entities/drfq/instruments';
import { DrfqInstruments, INITIAL_INSTRUMENTS_STATE } from './types';

function parseDate(date: Date | null): number {
  if (date === null) {
    throw new TypeError(
      "Instrument parse error: date shouldn't be null at this point",
    );
  }
  return date.getTime();
}

type InstrumentsFetcher = (
  venue: string,
  cb: (instruments: DrfqInstruments) => void,
) => void;

export function useDrfqInstrumentsFetcher(): InstrumentsFetcher {
  const { listInstruments } = useDrfqService();
  const [_, fetchInstruments] = useApi(listInstruments);
  const lastRequestAbortRef = useRef<AbortController>();

  const fetchDrfqInstruments: InstrumentsFetcher = useCallback(
    (venue, cb) => {
      lastRequestAbortRef.current?.abort();
      lastRequestAbortRef.current = new AbortController();
      fetchInstruments(
        {
          venue: venue as DrfqVenueCode,
          pageSize: 5000,
          signal: lastRequestAbortRef.current.signal,
        },
        (resp) => {
          if (!resp.ok) {
            cb(INITIAL_INSTRUMENTS_STATE);
            return;
          }

          cb(processInstruments(resp.data.results));
        },
      );
    },
    [fetchInstruments],
  );

  return fetchDrfqInstruments;
}

export function processInstruments(
  instruments: readonly Instrument[],
): DrfqInstruments {
  return {
    futureInstruments: instruments
      .filter(isFutureInstrument)
      .map((ins: Instrument) => ({
        ...ins,
        id: ins.id,
        expiration: ins.expires_at ? parseDate(ins.expires_at) : ins.expires_at,
        kind: 'FUTURE',
        greeks: ins.greeks!,
        option_kind: null,
        strike: null,
      })),
    optionInstruments: instruments
      .filter(isOptionInstrument)
      .map((ins: Instrument) => ({
        ...ins,
        id: ins.id,
        kind: 'OPTION',
        expiration: parseDate(ins.expires_at),
        greeks: ins.greeks!,
        option_kind: ins.option_kind ?? 'PUT',
        strike: ins.strike ?? '',
      })),
  };
}
